// Frontend-computed insights for Route Intelligence (spec pages 24, 28, 30, 34).

function avg(values) {
  const arr = (values || []).filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
  if (!arr.length) return null;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

// Section 4B: avg rate_headroom_pct for >300 mi vs <100 mi.
export function headroomInsight(rows) {
  if (!rows || !rows.length) return null;
  const longHaul = rows.filter((r) => Number(r.drive_distance_miles) > 300);
  const short = rows.filter((r) => Number(r.drive_distance_miles) < 100);
  const longAvg = avg(longHaul.map((r) => r.rate_headroom_pct));
  const shortAvg = avg(short.map((r) => r.rate_headroom_pct));
  if (longAvg == null || shortAvg == null) return null;
  return {
    longHaulAvg: Math.round(longAvg),
    shortAvg: Math.round(shortAvg),
    delta: Math.round(longAvg - shortAvg),
  };
}

// Section 5B: corridor with highest vs lowest avg_congestion_min (≥50 routes).
export function congestionCorridorInsight(corridors) {
  const eligible = (corridors || []).filter((c) => Number(c.routes) >= 50 && c.avg_congestion_min != null);
  if (eligible.length < 2) return null;
  let hi = eligible[0];
  let lo = eligible[0];
  for (const c of eligible) {
    if (c.avg_congestion_min > hi.avg_congestion_min) hi = c;
    if (c.avg_congestion_min < lo.avg_congestion_min) lo = c;
  }
  if (hi.corridor === lo.corridor) return null;
  return {
    worst: hi,
    best: lo,
    delta: Math.round((hi.avg_congestion_min - lo.avg_congestion_min) * 10) / 10,
  };
}

// Internal: pick the buckets that drive the ⚠ / ★ / 🔴 callouts. Used by
// both the in-table icons and the narrative below the table so they agree.
function pickMarketCallouts(buckets) {
  const rows = (buckets || []).filter((r) => r && r.bucket);
  let lowestConv = null;
  let highestConv = null;
  let highestValBooked = null;
  let highestValZero = null;
  for (const r of rows) {
    const conv = r.conversion_pct;
    const val = r.avg_booking_value ?? 0;
    if (r.booked === 0) {
      if (!highestValZero || val > (highestValZero.avg_booking_value ?? 0)) highestValZero = r;
    } else if (conv != null) {
      if (!lowestConv || conv < lowestConv.conversion_pct) lowestConv = r;
      if (!highestConv || conv > highestConv.conversion_pct) highestConv = r;
      if (!highestValBooked || val > (highestValBooked.avg_booking_value ?? 0)) highestValBooked = r;
    }
  }
  // Per spec p.30: ⚠ marks the bucket that is BOTH lowest conversion_pct AND
  // highest avg_booking_value among booked buckets. Skip if those two buckets
  // are different — the "paradox" only exists when one bucket has both traits.
  const warn = lowestConv && highestValBooked && lowestConv.bucket === highestValBooked.bucket
    && (highestConv == null || lowestConv.bucket !== highestConv.bucket)
    ? lowestConv
    : null;
  return { warn, star: highestConv, zero: highestValZero };
}

// Section 6A: ⚠ on lowest conversion_pct + highest avg_booking_value;
// ★ on highest conversion_pct; 🔴 on zero bookings.
export function marketAnnotations(buckets) {
  const { warn, star, zero } = pickMarketCallouts(buckets);
  return {
    warn: warn?.bucket || null,
    star: star?.bucket || null,
    zero: zero?.bucket || null,
  };
}

// Section 6A narrative (spec p.28): one bullet line per callout, prefixed by
// its glyph. Returns an array of { icon, bucket, text } so the UI can render
// each line with consistent styling.
export function marketNarrative(buckets) {
  const { warn, star, zero } = pickMarketCallouts(buckets);
  const out = [];
  if (warn) {
    out.push({ icon: '⚠', bucket: warn.bucket, text: 'Lowest conversion among bands with bookings.' });
  }
  if (star) {
    out.push({ icon: '★', bucket: star.bucket, text: 'Best conversion efficiency.' });
  }
  if (zero) {
    const sessions = zero.total_sessions ?? 0;
    out.push({ icon: '🔴', bucket: zero.bucket, text: `Dead zone — zero bookings from ${sessions} sessions.` });
  }
  return out;
}

// Fuel-summary shape adapter. fn_get_ri_fuel_summary now returns the park row
// and corridor rows split into `{ park, feeder_corridors }` instead of a single
// flat `waypoints` array. Rebuild the legacy array — park first (tagged
// waypoint_label 'property', which fuelArbitrageInsight keys on, and
// waypoint_type 'property'); each feeder corridor tagged 'feeder_corridor' — so
// existing consumers keep rendering. Passes a pre-existing `waypoints` array
// through unchanged in case the API ever returns the old shape.
/**
 * @param {object|null|undefined} summary Fuel-summary payload from the API.
 * @returns {Array<object>} Waypoint rows (park first, then feeder corridors), or [].
 */
export function fuelWaypoints(summary) {
  if (!summary || typeof summary !== 'object') return [];
  if (Array.isArray(summary.waypoints)) return summary.waypoints;
  const park = summary.park
    ? [{ ...summary.park, waypoint_label: 'property', waypoint_type: 'property' }]
    : [];
  const corridors = Array.isArray(summary.feeder_corridors)
    ? summary.feeder_corridors.map((c) => ({ ...c, waypoint_type: 'feeder_corridor' }))
    : [];
  return [...park, ...corridors];
}

// Section 7A: arbitrage between waypoints (property vs corridor).
export function fuelArbitrageInsight(waypoints) {
  const list = waypoints || [];
  if (list.length < 2) return null;
  const property = list.find((w) => w.waypoint_label === 'property');
  if (!property || property.avg_regular == null) return null;
  const others = list.filter((w) => w.waypoint_label !== 'property' && w.avg_regular != null);
  if (!others.length) return null;
  const corridorAvg = avg(others.map((w) => w.avg_regular));
  const diff = Math.round((corridorAvg - property.avg_regular) * 100) / 100;
  return {
    propertyAvg: property.avg_regular,
    corridorAvg: Math.round(corridorAvg * 100) / 100,
    diff,
    cheaperAt: diff > 0 ? 'property' : 'corridor',
  };
}

// Section 7D: top state by avg roundtrip fuel cost.
export function topFuelMarketInsight(rows) {
  if (!rows || !rows.length) return null;
  const sorted = [...rows].sort((a, b) => Number(b.avg_roundtrip_fuel_cost) - Number(a.avg_roundtrip_fuel_cost));
  const top = sorted[0];
  return { state: top.state, cost: Math.round(Number(top.avg_roundtrip_fuel_cost)) };
}
